<template>
  <div class="w-full">
    <h5 v-if="title != ''" class="text-left mb-2 mt-8 mr-4 ml-8">
      {{ title }}
    </h5>
    <div class="flex">
      <zoom-picker
        v-if="dates.start && dates.end && zoom && data.length >= MIN_VALUES_NUMBER"
        ref="zoom-picker"
        class="flex-grow py-2"
        :start-date="dates.start"
        :end-date="dates.end"
        :ytd-start-date="dates.ytdStart"
        :show-live-button="showLiveButtonZoom"
        v-on="$listeners"
      />
    </div>
    <div
      :id="'charts-' + _uid"
      :ref="'charts-' + _uid"
      style="width: 100%; position: relative"
      class="stacked-area-chart"
    ></div>
    <span v-if="data.length < MIN_VALUES_NUMBER" class="px-6 text-danger"
      >Not enough data to plot this graph, please select a longer date range.</span
    >
    <div
      v-if="legendsNumber && colorsFunc != null && data.length >= MIN_VALUES_NUMBER"
      class="flex-col text-xs md:text-sm"
    >
      <div v-for="number in legendsNumber" :key="number" class="flex items-center">
        <div
          :style="'width: 10px; height: 10px; background-color: ' + colorsFunc(keys[number - 1])"
        />
        <p class="pl-2">{{ legends[number - 1] }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { clearAllBodyScrollLocks, disableBodyScroll } from "body-scroll-lock";
import * as d3 from "d3";
import _ from "lodash";
import { isMobile } from "mobile-device-detect";
import moment from "moment";

import exports from "@/assets/scss/exports.module.scss";
import ZoomPicker from "@/components/datepickers/ZoomPicker";

import utils from "./utils";

export default {
  // Does not support value going from positive to negative or vice-versa.
  name: "StackedAreaChart",
  components: {
    ZoomPicker,
  },
  props: {
    title: {
      type: String,
      default: "",
    },
    // Array of objects, where the time field in each of those objects
    // should be called "date".
    data: {
      type: Array,
    },
    // Used to define the different zoom periods
    dates: {
      type: Object,
    },
    tickValues: {
      type: Array,
    },
    lineColors: {
      type: Array,
    },
    legends: {
      type: Array,
    },
    legendsNumber: {
      type: Number,
    },
    yDomain: {
      type: Array,
    },
    formatValue: {
      type: String,
      default: ",.2f",
    },
    parseTimeFormat: {
      type: String,
      default: "%Y-%m-%dT%H:%M:%S",
    },
    formatTime: {
      type: String,
      default: "%d %b %Y",
    },
    // Show the border line or not
    displayBorderLine: {
      type: Boolean,
      default: false,
    },
    // Opacity of the area
    opacityArea: {
      type: Number,
      default: 1,
    },
    // Add a zoom picker
    zoom: {
      type: Boolean,
      default: false,
    },
    // Whether we want to show 'Live' button in the zoom picker.
    showLiveButtonZoom: {
      type: Boolean,
      default: false,
    },
    // Optional list of dates at which we will draw a thick vertical line.
    datesForVerticalLines: {
      type: Array,
      default: undefined,
    },
    // Props related to the locked area.
    lockedRedirectUrl: {
      type: String,
    },
    lockedTextContent: {
      type: String,
    },
  },
  data() {
    return {
      MIN_VALUES_NUMBER: 2, // Minimum number of non nan values before showing the graph
      chart: null,
      selector: "",
      wait: null,
      currentWidth: 0,
      colorsFunc: null,
      keys: [],
      margin: null,
    };
  },
  watch: {
    data() {
      this.areaChart(true);
    },
  },
  beforeMount() {
    window.addEventListener("resize", this.resize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resize);
  },
  mounted() {
    this.selector = "charts-" + this._uid;
    this.chart = this.areaChart();
  },
  methods: {
    resize() {
      clearTimeout(this.wait);
      this.wait = setTimeout(this.areaChart, 200);
    },
    areaChart(force) {
      const selector = this.selector;
      const div = this.$refs[selector];
      const totalWidth = div.clientWidth;
      if (!force && totalWidth == this.currentWidth) return;
      this.currentWidth = totalWidth;
      this.currentHeight = document.body.clientHeight;
      div.innerHTML = "";
      // If less than 2 non nan values, don't draw anything
      if (this.data.length < this.MIN_VALUES_NUMBER) return;

      const DEFAULT_RATIO = 0.3; // On desktop
      const MOBILE_RATIO = 0.5; // On mobile
      const MOBILE_THRESHOLD_WIDTH = 700; // Min width for desktop

      let ratio = totalWidth < MOBILE_THRESHOLD_WIDTH ? MOBILE_RATIO : DEFAULT_RATIO;

      this.margin = {
        top: 5,
        right: 0,
        bottom: 0,
        left: 0,
      };

      const formatValue = this.$d3FormatLocale(this.formatValue);
      var data,
        svg,
        width,
        height,
        y,
        x,
        allDates,
        lastValidDate,
        yLow,
        yListHigh,
        hasNegativeValues,
        valuesName;
      for (let i = 2; i >= 0; i--) {
        data = _.cloneDeep(this.data);

        width = totalWidth - this.margin.left - this.margin.right;
        height = totalWidth * ratio - this.margin.top - this.margin.bottom;

        const parseTime = d3.timeParse(this.parseTimeFormat);

        const chart = d3
          .select("#" + selector)
          .append("svg")
          .attr("width", width + this.margin.left + this.margin.right)
          .attr("height", height + this.margin.top + this.margin.bottom);

        svg = chart
          .append("g")
          .attr("transform", "translate(" + this.margin.left + "," + this.margin.top + ")");

        // Get value name
        valuesName = Object.keys(data[0]).filter((key) => key !== "date");

        // Parse time.
        lastValidDate = null;

        // Parse time.
        data.forEach((d) => {
          d.date = parseTime(d.date);
          let allNull = true;
          valuesName.every((key) => {
            if (d[key] != null) {
              allNull = false;
              return false;
            }
            return true;
          });
          if (allNull && lastValidDate == null) {
            lastValidDate = d.date;
          }
          if (lastValidDate != null && !allNull) {
            lastValidDate = null;
          }
        });

        // Get the series that are negative for managing the case with the 0 value (putting under 0 or not)
        hasNegativeValues = {};

        valuesName.forEach((value) => {
          let i = 0;
          while (data[i][value] == 0 && i < data.length - 1) i = i + 1;
          hasNegativeValues[value] = data[i][value] < 0;
        });

        this.keys = valuesName;
        // Save all dates.
        allDates = data.map((d) => d.date);

        // Set colors for lines.
        if (this.lineColors) {
          this.colorsFunc = d3.scaleOrdinal().range(this.lineColors).domain(valuesName);
        } else {
          this.colorsFunc = d3.scaleOrdinal(d3.schemeCategory10).domain(valuesName);
        }
        x = d3
          .scaleTime()
          .domain(
            d3.extent(data, function (d) {
              return d.date;
            })
          )
          .range([0, width]);

        // Define the x-axis (format and interval of dates to show as ticks).
        const xAxis = d3.axisBottom(x).tickFormat(utils.formatDateTick(allDates));
        xAxis.ticks(utils.getTimeIntervalForTicks(allDates));

        // Add the X Axis.
        const dateTicks = svg
          .append("g")
          .attr("transform", "translate(0," + height + ")")
          .attr("class", "x axis")
          .style("font-family", exports["typography-font-family-sans-serif"])
          .call(xAxis)
          .selectAll(".tick");

        // Removing overlapping x axis labels.
        utils.removeOverlappingXLabels(dateTicks);

        let neededPxOnBottom = 0,
          neededPxOnRight = 0,
          neededPxOnLeft = 0;
        if (i != 0) {
          const axisBounding = dateTicks._parents[0].getBoundingClientRect();
          const divBounding = div.getBoundingClientRect();
          neededPxOnBottom = axisBounding.height;
          neededPxOnRight = Math.max(
            0,
            axisBounding.x + axisBounding.width - (divBounding.x + divBounding.width)
          );
          if (divBounding.x > axisBounding.x) {
            neededPxOnLeft = divBounding.x - axisBounding.x;
          }
        }

        y = d3.scaleLinear().range([height, 0]);

        // Handle domain of the graph.
        yLow = 0;
        let yHigh = 0;
        yListHigh = data.map(function (d) {
          let max = 0;
          valuesName.forEach((value) => {
            if (d[value] > 0) max += +d[value];
          });
          return max;
        });
        if (this.yDomain) {
          y.domain(this.yDomain);
          yLow = this.yDomain[0];
          yHigh = this.yDomain[1];
        } else {
          yLow = d3.min(data, function (d) {
            let min = 0;
            valuesName.forEach((value) => {
              if (d[value] < 0) min += d[value];
            });
            return min;
          });

          yHigh = d3.max(yListHigh);
          y.domain([yLow, yHigh]);
        }
        // Add the Y Axis.
        const yAxis = d3.axisLeft(y).tickSize(-width, 0, 0).tickPadding(10).tickFormat(formatValue);
        if (!this.tickValues) {
          yAxis.tickValues(y.ticks(4));
        } else {
          yAxis.tickValues(this.tickValues);
        }

        // Draw at the end y axis to be in front of area
        const yTicks = svg
          .append("g")
          .attr("class", "y axis")
          .style("font-family", exports["typography-font-family-sans-serif"])
          .call(yAxis)
          .selectAll(".tick");

        if (i != 0) {
          const axisBounding = yTicks._parents[0].getBoundingClientRect();
          const divBounding = div.getBoundingClientRect();
          neededPxOnLeft = Math.max(neededPxOnLeft, axisBounding.width - divBounding.width);
        }

        // If we were the first render, we now have all the desired margin,
        // we can remove the svg and re-render it.
        if (i != 0) {
          // TODO check why for Safari 10.1.2 it's a necessity.
          if (!neededPxOnRight) {
            neededPxOnRight = 0;
          }
          if (!neededPxOnLeft) {
            neededPxOnLeft = 0;
          }
          if (!neededPxOnBottom) {
            neededPxOnBottom = 0;
          }

          if (this.margin.bottom == 0) {
            this.margin.bottom = Math.ceil(neededPxOnBottom) + this.margin.bottom;
          }
          this.margin.right = Math.ceil(neededPxOnRight) + this.margin.right;
          this.margin.left = Math.ceil(neededPxOnLeft) + this.margin.left;
          chart.remove();
        }
      }

      const formatTime = d3.timeFormat(this.formatTime);

      var stackedValues = d3.stack().keys(valuesName)(data);
      const stackedData = [];
      // Create our own stacked data array to have access to the valuename and the dict hasNegativeValues.
      stackedValues.forEach((layer, index) => {
        const currentStack = [];
        layer.forEach((d) => {
          currentStack.push({
            values: [d[0], d[1]], // The calculate position of the stacked area (used for y0 and y1)
            date: d.data.date,
            key: valuesName[index],
          });
        });
        stackedData.push(currentStack);
      });
      svg
        .selectAll("mylayers")
        .data(stackedData)
        .enter()
        .append("path")
        .style("fill", (d) => {
          return this.colorsFunc(d[0].key);
        })
        .attr("fill-opacity", this.opacityArea)
        .attr(
          "d",
          d3
            .area()
            .x((d) => {
              return x(d.date);
            })
            .y0((d, i) => {
              if (hasNegativeValues[d.key] && d.values[0] >= d.values[1])
                return y(d.values[0] - yListHigh[i]);
              return y(d.values[0]);
            })
            .y1((d, i) => {
              if (hasNegativeValues[d.key] && d.values[0] >= d.values[1])
                return y(d.values[1] - yListHigh[i]);
              return y(d.values[1]);
            })
        );

      // Add thick vertical lines if provided.
      if (this.datesForVerticalLines != undefined) {
        for (const dateFortVerticalLine of this.datesForVerticalLines) {
          svg
            .append("path") // this is the black vertical line to follow mouse
            .attr("d", () => {
              // Return from where to where we want the line to be drawn
              // (same X, but 0 to 'height').
              return (
                "M" +
                x(dateFortVerticalLine) +
                "," +
                height +
                " " +
                x(dateFortVerticalLine) +
                "," +
                0
              );
            })
            .style("stroke", exports["colors-alquant"])
            .style("stroke-width", "2px");
        }
      }

      // Handle tooltip and mouse interactions.
      const tooltip = d3
        .select("#" + selector)
        .append("div")
        .attr("class", "tooltip-graph text-xs md:text-sm text-black")
        .style("z-index", 1)
        .style("position", "absolute")
        .style("padding", 6)
        .style("display", "none");
      const mouseG = svg.append("g").attr("class", "mouse-over-effects");

      mouseG
        .append("path") // this is the black vertical line to follow mouse
        .attr("class", "mouse-line")
        .style("stroke", "black")
        .style("stroke-width", "1px")
        .style("opacity", "0");

      // Create function that updates the tooltip, vertical line and circles.
      const updateTooltipLineAndCircles = (event, target) => {
        // We should not assume that all brush events are done by user, and in
        // this case we won't have a sourceEvent, which means we shouldn't
        // do anything and stop the update.
        // https://github.com/d3/d3-selection/issues/122
        if (event.type == "brush" && !event.sourceEvent) {
          return;
        }

        // Note that d3.pointer needs a Touch event, and not a TouchEvent event to
        // work correctly.
        // https://github.com/d3/d3-selection/issues/245
        // But note that TouchEvent isn't known to Safari and Firefox.
        // https://developer.mozilla.org/en-US/docs/Web/API/TouchEvent/TouchEvent
        if (event.touches) event = event.touches[0];
        const mouse = d3.pointer(event, target);

        // Use 'invert' to get date corresponding to mouse position
        // relative to svg.
        // Note: This function can sometimes return a value that is outside of our
        // date range.
        const xDate = x.invert(mouse[0]);
        // Get index corresponding to the date the closest from the mouse.
        const dateIdx = utils.getDateIndex(allDates, xDate);
        // Add a small circle on each displayed line (on the index the mouse is).
        svg
          .selectAll("circle")
          .attr("transform", (d) => {
            // Return the coordinates of where to draw the circle for this line.
            // Skip the undefined data to avoid throwing errors. This will still
            // draw them, but at the top-left of the chart, and then after we
            // will make them invisble by setting their radius to 0.
            if (d.values[dateIdx].value != null) {
              return (
                "translate(" + x(d.values[dateIdx].date) + "," + y(d.values[dateIdx].value) + ")"
              );
            }
          })
          // Draw the circles with 0 radius (invisible) if the value is null.
          .attr("r", (d) => (d.values[dateIdx].value != null ? this.radiusCircleOnLine : 0));

        // Add the vertical line on the closest date from the mouse.
        svg.select(".mouse-line").attr("d", () => {
          // Return from where to where we want the line to be drawn
          // (same X, but 0 to 'height').
          return "M" + x(allDates[dateIdx]) + "," + height + " " + x(allDates[dateIdx]) + "," + 0;
        });

        // Get values of each line at the the date closest to the mouse; this
        // will be used for displaying in the tooltip.
        const sortingObj = [];

        this.keys.forEach((d) => {
          sortingObj.push({
            name: d,
            value: data[dateIdx][d] != null ? +data[dateIdx][d] : null,
            date: formatTime(data[dateIdx].date),
          });
        });

        // be constants and have a name that is easily understandable?
        const tooltipBouding = tooltip.node().getBoundingClientRect();
        // Define the position of the tooltip.
        const { right, left, inverseY } = utils.getTooltipPosition(
          width,
          height,
          mouse,
          tooltipBouding,
          y(yLow)
        );
        tooltip
          .html(sortingObj[0].date)
          .style("display", "block")
          .style("left", left)
          .style("top", inverseY + "px")
          .style("right", right)
          .style("font-weight", 500)
          .selectAll()
          .data(sortingObj)
          .enter()
          .append("div")
          .style("color", (d) => {
            if (!this.tooltipColor) {
              return this.colorsFunc(d.name);
            } else {
              const value = this.tooltipColor(d.name, d.value);
              if (!value) return this.colorsFunc(d.name);
              return value;
            }
          })
          .style("font-size", 10)
          .html((d, i) => {
            // Get name of the line.
            let lineName = d.name;
            if (this.legends && this.legends[i]) lineName = this.legends[i];
            // Get the formatted data (display "N/A" if not available, i.e. null).
            let formattedValue = formatValue(d.value);
            if (d.value == null) formattedValue = "N/A";
            // Display the line on the tooltip.
            return lineName + ": <b>" + formattedValue + "</b>";
          });
      };

      let hover;
      // No zoom on mobile
      if (!this.zoom || isMobile) {
        hover = mouseG
          .append("svg:rect")
          .attr("width", width)
          .attr("height", height)
          .attr("fill", "none")
          .attr("pointer-events", "all");
      } else {
        svg
          .append("defs")
          .append("svg:clipPath")
          .attr("id", "clip-" + this._uid)
          .append("svg:rect")
          .attr("width", width)
          .attr("height", height)
          .attr("x", 0)
          .attr("y", 0);
        hover = mouseG.attr("clip-path", `url(#clip-${this._uid})`);
      }
      hover
        .on("mouseout touchend", () => {
          // on mouse out hide line, circles and text
          svg.select(".mouse-line").style("opacity", "0");
          svg.selectAll(".mouse-per-line circle").style("opacity", "0");
          svg.selectAll(".mouse-per-line text").style("opacity", "0");
          tooltip.style("display", "none");
          clearAllBodyScrollLocks();
        })
        .on("mouseover touchstart", function (event) {
          // on mouse in show line, circles and text
          svg.select(".mouse-line").style("opacity", "1");
          svg.selectAll(".mouse-per-line circle").style("opacity", "1");
          tooltip.style("display", "block");
          // Disable scroll for small devices (e.g. mobile).
          if (window.innerWidth < 800) disableBodyScroll(d3.select("#" + selector));
          // Also call `updateStuff` because otherwise if somebody scrolls to the graph
          // but doesn't use their mouse, we won't see the tooltip, line, and circles
          // correctly.
          updateTooltipLineAndCircles(event, this);
        })
        // update tooltip content, line, circles and text when mouse moves
        .on("mousemove touchmove", function (event) {
          updateTooltipLineAndCircles(event, this);
        });
      // Draw at the end y axis to be in front of area
      /*
      svg
        .append("g")
        .attr("class", "y axis")
        .style("font-family", exports["typography-font-family-sans-serif"])
        .call(yAxis);
        */
      // Brush on graph only if the props zoom is true and it is not a mobile phone
      if (this.zoom && !isMobile) {
        // Create the clip to do the brush effect

        const brush = d3
          .brushX() // Add the brush feature using the d3.brush function
          .extent([
            [0, 0],
            [width, height],
          ]); // initialise the brush area: start at 0,0 and finishes at width,height
        // Add the brushing
        hover.append("g").attr("class", "brush").call(brush);

        // Add a timeout to be able to send the date change before the reset of the period
        let idleTimeout;
        const idled = () => {
          idleTimeout = null;
        };

        // A function that update the chart with the given period
        const updateChart = (event) => {
          // What are the selected boundaries?
          const extent = event.selection;
          // If no selection, back to initial coordinate. Otherwise, update X axis domain
          if (!extent) {
            // This allows to wait a little bit
            if (!idleTimeout) return (idleTimeout = setTimeout(idled, 350));
            this.$emit(
              "dates-change",
              {
                start: null,
                end: null,
              },
              { method: "zoom-reset" }
            );
          } else {
            // This removes the grey brush area as soon as the selection has been done.
            mouseG.select(".brush").call(brush.move, null);

            // Then we only want to zoom if the two dates are not exactly the same (i.e.
            // we have at least 2 dates in our selection).
            const zoomStartDate = allDates[utils.getDateIndex(allDates, x.invert(extent[0]))];
            const zoomEndDate = allDates[utils.getDateIndex(allDates, x.invert(extent[1]))];
            if (zoomEndDate - zoomStartDate > 0) {
              const datesChange = {
                start: moment(zoomStartDate).format("YYYY-MM-DD"),
                end: moment(zoomEndDate).format("YYYY-MM-DD"),
              };
              this.$emit("dates-change", datesChange, { method: "zoom" });
            }
          }
        };
        brush.on("brush", function (event) {
          updateTooltipLineAndCircles(event, this);
        });
        brush.on("end", updateChart);
      }

      // Check if user has not access to the latest data, and if it is the case,
      // show a lock image with text to inform the user.
      if (lastValidDate != null) {
        const newLastValidDate = allDates[allDates.indexOf(lastValidDate) - 1];
        // Only reassign if we find another non-null value.
        if (newLastValidDate) {
          lastValidDate = newLastValidDate;
        }
      }

      if (lastValidDate) {
        const xLastDate = x(lastValidDate);
        const svgWidth = width - xLastDate;

        // If svg width is smaller than 80px, we do not show the lock image
        const showLockAndText = svgWidth > 80;

        // Defining the size of the lock image
        const initialSizeLock = 30;
        let sizeLock = initialSizeLock;

        // If the size of the no-acccess svg is smaller than 3 times the size of the lock,
        // set the lock to 3 times smaller than the svg size.
        // This is because the lock size is too big instead.
        if (svgWidth < 90) {
          sizeLock = svgWidth / 3;
        }
        // Defining the width of the text box.
        let textWidth = 200;
        if (svgWidth < 200) {
          textWidth = svgWidth - 10;
        }

        // Defining the margin between the text and the lock screen.
        // On small device, we define smaller margin.
        let marginText = 20;
        if (sizeLock < initialSizeLock) {
          marginText = 10;
        }
        // Show lock image and text
        utils.addLockToGraph(
          svg,
          this.lockedRedirectUrl,
          xLastDate,
          svgWidth,
          height,
          showLockAndText,
          sizeLock,
          textWidth,
          marginText,
          this.lockedTextContent,
          selector
        );
      }
      return svg;
    },
  },
};
</script>

<style>
#zero_line {
  stroke: #000;
  stroke-width: 0.4;
}
</style>
